import { EyeOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Button, Carousel, Image } from 'antd';
import { FC, useCallback } from 'react';
import { FileCardGallery, useFilesUrlWithToken } from 'services';
import {
  useFilesGalleryViewType,
  useSelectedFileToView,
  useSelectedImageToView,
  useSetSelectedFileToView,
  useSetSelectedImageToView,
} from '../../store/files-gallery.store';
import { FileIcon } from '../FileIcon';
import { FileInfoModal } from '../FileInfoModal/FileInfoModal';
import styles from './Gallery.module.scss';
import { carouselOptions } from './options';

export interface GalleryProps {
  files?: FileCardGallery[];
}

const Gallery: FC<GalleryProps> = ({ files }) => {
  const viewType = useFilesGalleryViewType();
  const { fileUrlWithToken, fileUrlWithCroppingParamAndToken } = useFilesUrlWithToken();
  const selectedFileToView = useSelectedFileToView();
  const setSelectedFileToView = useSetSelectedFileToView();
  const setSelectedImageToView = useSetSelectedImageToView();
  const selectedImageToView = useSelectedImageToView();

  const handleFileListClick = useCallback(
    (itemFile: FileCardGallery) => {
      if (itemFile.type === 'image') {
        setSelectedImageToView(itemFile);
      } else {
        setSelectedFileToView(itemFile);
      }
    },
    [setSelectedFileToView, setSelectedImageToView],
  );

  if (!files || !files.length) return null;

  return (
    <div className={styles.galleryContainer}>
      {viewType === 'list' ? (
        <div className={styles.galleryListDisplayContainer}>
          {files?.map((file) => (
            <Button
              key={file?.id}
              type="link"
              icon={<PaperClipOutlined style={{ color: 'var(--gray-8)' }} />}
              onClick={handleFileListClick.bind(null, file)}
            >
              {file?.name}.{file?.extension}
            </Button>
          ))}
        </div>
      ) : (
        <Image.PreviewGroup>
          <Carousel className={styles.containerCarousel} {...carouselOptions}>
            {files?.map((file) => (
              <div key={file?.id} className={styles.carouselItem}>
                {file?.type === 'image' ? (
                  <Image
                    alt={file?.name}
                    src={fileUrlWithCroppingParamAndToken(file?.url, { format: 'thumbnail-lg' }) || ''}
                    preview={{ src: fileUrlWithToken(file?.url) || undefined }}
                  />
                ) : (
                  <div className={styles.carouselFileItem}>
                    <button
                      type="button"
                      className={styles.seeFileButton}
                      onClick={() => {
                        setSelectedFileToView(file);
                      }}
                    >
                      <EyeOutlined /> Preview
                    </button>
                    <FileIcon ext={file.extension} />
                  </div>
                )}
              </div>
            ))}
          </Carousel>
        </Image.PreviewGroup>
      )}

      {selectedImageToView && (
        <Image
          style={{ display: 'none' }}
          src={fileUrlWithToken(selectedImageToView.url) || ''}
          alt={selectedImageToView.name}
          preview={{
            visible: true,
            src: fileUrlWithToken(selectedImageToView.url) || '',
            onVisibleChange: (_value) => {
              setSelectedImageToView(undefined);
            },
          }}
        />
      )}

      {selectedFileToView && (
        <FileInfoModal
          file={selectedFileToView}
          close={() => setSelectedFileToView(undefined)}
          open={!!selectedFileToView}
        />
      )}
    </div>
  );
};

export { Gallery };
